import {AxiosPromise, AxiosResponse} from 'axios'

import {SupportedLocale} from 'core/api'
import Client from 'core/api/client'
import {IPrescriptionJson} from 'core/api/prescription/interfaces'
import {IanaTimezone} from 'core/utils/timezone'
import {PatientId} from './interfaces'

/**
 * Represents a single day's worth of service usage
 */
export interface IDailyUsage {
  /** The day (relative to the patient's timezone) on which the treatment was performed - Of the form YYYY-MM-DD */
  day: string

  /** The total treatment received in milliseconds. It is possible for this to be higher than the the daily dosage. */
  completedDosage: number
}

/**
 * Represents suitable response data for a single prescription
 */
export interface IPrescriptionData extends IPrescriptionJson {
  /** Flag to determine if this prescription is currently active */
  isActive: boolean
}

/**
 * Represents prescription and treatment data for a single prescription
 */
export interface ITreatmentReportPrescriptionData extends IPrescriptionData {
  /** Daily usage data, one per day, for the given prescription */
  dailyUsages: IDailyUsage[]
}

/** Adherence data fields for a treatment report */
export interface ITreatmentReportAdherenceData {
  /** The number of milliseconds of prescribed treatment for the adherence time period. */
  adherenceDosage: number

  /** The number of milliseconds of completed treatment for the adherence time period. */
  adherenceCompletedDosage: number

  /**
   * Whether the subject is treatment-adherent (i.e. has been completing a minimum amount of treatment)
   * in the adherence time period.
   */
  isTreatmentAdherent: boolean
}

/** Patient data fields for a treatment report */
export interface ITreatmentReportPatientData {
  /** The patient's simple mode setting */
  isSimpleMode: boolean

  /** Flag signifying if the patient currently has any active prescriptions */
  isTreatmentActive: boolean

  /** The patient's language setting (ex: en_US) */
  language: SupportedLocale

  /** The patient's timezone (ex: America/New_York) */
  timezone: IanaTimezone

  /**
   * The current UTC offset for the patient's timezone in seconds (e.g. `-3600` for UTC-1:00).
   * Note that this can change for a given timezone due to factors such as Daylight Savings Time.
   */
  utcOffset: number
}

export type DailyDosageJson = {
  // ISO-8601 format uuuu-MM-dd
  date: string,
  prescriptionId: string | null,
  dailyDosage: number | null,
  daysPerWeekDosage: number | null,
}

/** A full treatment report for a given patient */
export interface IPatientTreatmentReport extends ITreatmentReportAdherenceData, ITreatmentReportPatientData {
  /** The unique identifier for the patient */
  patientId: PatientId

  /** Information for individual patient prescriptions */
  prescriptions: ITreatmentReportPrescriptionData[]

  /** Effective dosage data for each day in the requested time range */
  dailyDosages: DailyDosageJson[]

  /**
   * Effective total recommended dosage for the requested time range in ms.
   * Calculates based on the prescription days recommended per week and the prescription daily dosage.
   */
  totalRecommendedDosage: number
}

/** Interface representing the response body for the patient treatment report */
export interface IPatientTreatmentReportResponse extends AxiosResponse {
  data: {
    treatmentReport: IPatientTreatmentReport,
  }
}

/**
 * GET /patients/{id}/treatment-report
 * Fetches the treatment report for the requested patient
 * @param token: The authorization token used for authenticating the request
 * @param id: The id of the patient to get the report. Defaults to the special identifier `me`, which will return
 *   the report for the currently authenticated patient.
 */
export function getTreatmentReport(token: string, id: string = 'me'): AxiosPromise<IPatientTreatmentReportResponse> {
  const headers = { Authorization: `Bearer ${token}` }
  return Client.get(`/patients/${id}/treatment-report`, { headers, validateStatus: () => true })
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./core/src/api/patient/treatment.ts